import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  TableColumnProps,
  Tag
} from '@arco-design/web-react';
import { debounce } from 'lodash';
import dayjs from 'dayjs';

import { get, post } from '../requestNew';
import PhoneCell from '../components/PhoneCell';

import './index.scss';

type CardType = 1 | 2 | 3 | 4;
type OrderStatus = 'SUCCESS' | 'NOTPAY' | 'REFUND';

type UmsUserMember = {
  id: number;
  openId: string;
  oneId?: string;
  gameName: string;
  phone: string;
  orderId: string;
  orderStatus?: OrderStatus | string;
  cardNo: string;
  cardType: CardType;
  cardStatus: number;
  openTime: string;
  expireTime: string;
  reviewGroupId?: number;
  isValid: number;
  remark?: string;
  createTime?: string;
  updateTime?: string;
};

type ReviewGroupOption = {
  id: number;
  groupName: string;
  status?: number;
  isValid?: number;
};

type UserOption = {
  label: string; // gameName
  value: string; // openId/openId
  phone?: string;
};

type UmsMemberCard = {
  id: number;
  cardType: number;
  cardCode: string;
  cardName: string;
  cardTag: string;
  price: number;
  durationDays: number;
  dailyPrice: string;
  description: string;
  isValid: number;
};

const Option = Select.Option;
const RangePicker = DatePicker.RangePicker;
const MONTHLY_CARD_TYPE: CardType = 2;

// 卡券类型标签配色（参照设计稿：填充色 + 白字）
const cardTypeMap: Record<CardType, { label: string; color: string; bgColor: string }> = {
  1: { label: '周卡', color: '#FFFFFF', bgColor: '#00B42A' },
  2: { label: '月卡', color: '#FFFFFF', bgColor: '#FFB020' },
  3: { label: '季卡', color: '#FFFFFF', bgColor: '#E8590C' },
  4: { label: '年卡', color: '#FFFFFF', bgColor: '#F5222D' }
};

const orderStatusFilterOptions = [
  { label: '全部', value: '' },
  { label: '支付成功', value: 'SUCCESS' },
  { label: '未支付', value: 'NOTPAY' },
  { label: '转入退款', value: 'REFUND' }
] as const;

const orderStatusLabelByValue: Record<string, string> = Object.fromEntries(
  orderStatusFilterOptions.filter(item => item.value !== '').map(({ value, label }) => [value, label])
);

const UserMember: React.FC = () => {
  const [filterForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const [editGroupForm] = Form.useForm();
  const [deleteForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [dataList, setDataList] = useState<UmsUserMember[]>([]);

  const [addVisible, setAddVisible] = useState(false);
  const [addConfirmLoading, setAddConfirmLoading] = useState(false);
  const [editGroupVisible, setEditGroupVisible] = useState(false);
  const [editGroupLoading, setEditGroupLoading] = useState(false);
  const [editingMember, setEditingMember] = useState<UmsUserMember | null>(null);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [deletingMember, setDeletingMember] = useState<UmsUserMember | null>(null);

  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [userFetching, setUserFetching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [reviewGroupMap, setReviewGroupMap] = useState<Record<number, string>>({});
  const [reviewGroupOptions, setReviewGroupOptions] = useState<ReviewGroupOption[]>([]);
  const [memberCardList, setMemberCardList] = useState<UmsMemberCard[]>([]);
  const refFetchId = useRef(0);
  const addCardType = Form.useWatch('cardType', addForm);
  const isMonthlyCard = addCardType === MONTHLY_CARD_TYPE;

  const enabledMemberCards = useMemo(
    () => memberCardList.filter(item => item.isValid === 1),
    [memberCardList]
  );

  const getCardTypeInfo = useCallback(
    (cardType: CardType) => {
      const card = memberCardList.find(item => item.cardType === cardType);
      const fallback = cardTypeMap[cardType];
      return {
        label: card?.cardName || fallback?.label || '-',
        color: fallback?.color || '#FFFFFF',
        bgColor: fallback?.bgColor || '#86909C'
      };
    },
    [memberCardList]
  );

  const cardStatusFixed = 1; // 当前只查询：已使用

  const getList = useCallback(
    () => {
      setLoading(true);
      const values = filterForm.getFieldsValue() as {
        gameName?: string;
        phone?: string;
        openId?: string;
        oneId?: string;
        orderId?: string;
        cardType?: CardType | '' | undefined;
        reviewGroupId?: number | '' | undefined;
        orderStatus?: OrderStatus | '' | undefined;
        openTimeRange?: (string | Date)[];
        expireTimeRange?: (string | Date)[];
        isValid?: 'true' | 'false' | '' | undefined;
      };

      const params: Record<string, any> = {
        cardStatus: cardStatusFixed,
        pageNum,
        pageSize
      };

      if (values?.gameName) params.gameName = values.gameName;
      if (values?.phone) params.phone = values.phone;
      const openIdTrim = values?.openId != null ? String(values.openId).trim() : '';
      if (openIdTrim) params.openId = openIdTrim;
      const oneIdTrim = values?.oneId != null ? String(values.oneId).trim() : '';
      if (oneIdTrim) params.oneId = oneIdTrim;
      if (values?.orderId) params.orderId = values.orderId;
      if (values?.orderStatus) params.orderStatus = values.orderStatus;
      if (values?.cardType !== undefined && values.cardType !== '') params.cardType = Number(values.cardType);
      if (values?.reviewGroupId !== undefined && values.reviewGroupId !== '') {
        params.reviewGroupId = Number(values.reviewGroupId);
      }
      const openTimeRange = values.openTimeRange || [];
      if (openTimeRange.length === 2) {
        params.openStartTime = dayjs(openTimeRange[0]).format('YYYY-MM-DD');
        params.openEndTime = dayjs(openTimeRange[1]).format('YYYY-MM-DD');
      }
      const expireTimeRange = values.expireTimeRange || [];
      if (expireTimeRange.length === 2) {
        params.expireStartTime = dayjs(expireTimeRange[0]).format('YYYY-MM-DD');
        params.expireEndTime = dayjs(expireTimeRange[1]).format('YYYY-MM-DD');
      }
      if (values?.isValid !== undefined && values.isValid !== '') params.isValid = values.isValid === 'true';

      get('/review/userMember/list', params)
        .then((res: any) => {
          const { code, data, message } = res || {};
          if (code === 200) {
            const { list = [], total: totalCount } = data || {};
            setDataList(list || []);
            setTotal(totalCount || 0);
          } else {
            Message.error(message || '获取数据失败!');
          }
        })
        .catch(() => {
          Message.error('网络错误!');
        })
        .finally(() => setLoading(false));
    },
    [filterForm, pageNum, pageSize]
  );

  useEffect(() => {
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, pageSize]);

  useEffect(() => {
    get('/review/groupQrcode/list')
      .then((res: any) => {
        if (res?.code === 200 && res?.data?.list) {
          const list = (res.data.list as ReviewGroupOption[]).filter(item => item?.id != null && item.isValid !== 0);
          const map: Record<number, string> = {};
          list.forEach(item => {
            map[item.id] = item.groupName || '';
          });
          setReviewGroupMap(map);
          setReviewGroupOptions(list);
        }
      })
      .catch(() => {
        // 复盘群名称仅用于展示，失败时不阻塞列表
      });
  }, []);

  useEffect(() => {
    get('/review/memberCard/list')
      .then((res: any) => {
        if (res?.code === 200) {
          setMemberCardList(res?.data || []);
        }
      })
      .catch(() => {
        // 会员卡配置仅用于下拉选项，失败时不阻塞列表
      });
  }, []);

  const getReviewGroupName = useCallback(
    (reviewGroupId?: number) => {
      if (reviewGroupId == null) return '-';
      return reviewGroupMap[reviewGroupId] || '-';
    },
    [reviewGroupMap]
  );

  const renderReviewGroupOptions = (disabledGroupId?: number) =>
    reviewGroupOptions.map(item => (
      <Option key={item.id} value={item.id} disabled={item.id === disabledGroupId}>
        {item.groupName}
        {item.status === 1 ? '（使用中）' : ''}
      </Option>
    ));

  const debouncedFetchUsers = useCallback(
    debounce((keyword: string) => {
      const safeKeyword = (keyword || '').trim();
      if (!safeKeyword) {
        setUserOptions([]);
        return;
      }

      refFetchId.current = Date.now();
      const fetchId = refFetchId.current;
      setUserFetching(true);
      setUserOptions([]);

      // 这里沿用勋章管理“绑定勋章”里的查询用户逻辑：GET /backend/umsUser/list?gameName=xxx
      get('/backend/umsUser/list', { gameName: safeKeyword, pageNum: 1, pageSize: 20 })
        .then((res: any) => {
          const { code, data } = res || {};
          if (refFetchId.current !== fetchId) return;
          if (code === 200) {
            const list = data?.list || [];
            const options = list.map((item: any) => {
              return {
                label: item.gameName || '',
                value: item.openId || '',
                phone: item.phone
              };
            });
            setUserOptions(options);
          }
        })
        .catch(() => {
          // 忽略：只在用户下拉为空时不展示内容即可
        })
        .finally(() => setUserFetching(false));
    }, 500),
    []
  );

  const fetchAddOrderId = useCallback(() => {
    get('/review/userMember/generateOrderId')
      .then((res: any) => {
        if (res?.code === 200 && res?.data) {
          addForm.setFieldValue('orderId', res.data);
        }
      })
      .catch(() => {
        // 提交时后端仍会兜底生成
      });
  }, [addForm]);

  const openAddModal = () => {
    setSelectedUser(null);
    setUserOptions([]);
    setAddVisible(true);

    addForm.resetFields();
    const defaultCardType =
      enabledMemberCards.find(item => item.cardType === MONTHLY_CARD_TYPE)?.cardType ??
      enabledMemberCards[0]?.cardType ??
      MONTHLY_CARD_TYPE;
    addForm.setFieldsValue({
      cardStatus: cardStatusFixed,
      cardType: defaultCardType,
      reviewGroupId: undefined,
      orderId: ''
    });
    fetchAddOrderId();
  };

  const handleQuery = useCallback(() => {
    if (pageNum !== 1) {
      setPageNum(1);
    } else {
      getList();
    }
  }, [getList, pageNum]);

  const handleReset = useCallback(() => {
    filterForm.resetFields();

    const shouldResetPagination = pageNum !== 1 || pageSize !== 20;
    if (shouldResetPagination) {
      setPageNum(1);
      setPageSize(20);
      return;
    }

    // 已经是初始化分页，手动拉一次
    getList();
  }, [filterForm, getList, pageNum, pageSize]);

  const closeAddModal = () => {
    setAddVisible(false);
    setAddConfirmLoading(false);
    setSelectedUser(null);
    addForm.resetFields();
    setUserOptions([]);
  };

  const submitAdd = () => {
    if (!selectedUser) {
      Message.error('请选择用户');
      return;
    }
    addForm
      .validate()
      .then(values => {
        const payload = {
          openId: values.openId,
          gameName: selectedUser.label,
          phone: selectedUser.phone || values.phone,
          orderId: values.orderId || undefined,
          cardType: Number(values.cardType),
          cardStatus: cardStatusFixed,
          reviewGroupId:
            Number(values.cardType) === MONTHLY_CARD_TYPE ? undefined : Number(values.reviewGroupId),
          remark: values.remark || undefined
        };

        setAddConfirmLoading(true);
        post('/review/userMember/add', payload)
          .then((res: any) => {
            const { code, message } = res || {};
            if (code === 200) {
              Message.success('新增会员成功！');
              closeAddModal();
              getList();
            } else {
              Message.error(message || '新增会员失败！');
            }
          })
          .catch(() => {
            Message.error('网络错误!');
          })
          .finally(() => setAddConfirmLoading(false));
      })
      .catch(() => {
        Message.error('请检查表单填写是否正确!');
      });
  };

  const openEditGroupModal = (record: UmsUserMember) => {
    setEditingMember(record);
    editGroupForm.resetFields();
    setEditGroupVisible(true);
  };

  const closeEditGroupModal = () => {
    setEditGroupVisible(false);
    setEditGroupLoading(false);
    setEditingMember(null);
    editGroupForm.resetFields();
  };

  const submitEditGroup = () => {
    if (!editingMember?.openId) {
      Message.error('openId不存在');
      return;
    }
    editGroupForm
      .validate()
      .then(values => {
        const nextReviewGroupId = Number(values.reviewGroupId);
        if (nextReviewGroupId === editingMember.reviewGroupId) {
          Message.warning('请选择其他复盘群');
          return;
        }
        setEditGroupLoading(true);
        post('/review/userMember/updateReviewGroup', {
          openId: editingMember.openId,
          reviewGroupId: nextReviewGroupId
        })
          .then((res: any) => {
            const { code, message } = res || {};
            if (code === 200) {
              Message.success('修改群组成功！');
              closeEditGroupModal();
              getList();
            } else {
              Message.error(message || '修改群组失败！');
            }
          })
          .catch(() => {
            Message.error('修改群组失败！');
          })
          .finally(() => setEditGroupLoading(false));
      })
      .catch(() => {
        Message.error('请选择目标复盘群');
      });
  };

  const openDeleteModal = (record: UmsUserMember) => {
    if (!record?.id) {
      Message.error('会员ID不存在！');
      return;
    }
    setDeletingMember(record);
    deleteForm.resetFields();
    setDeleteVisible(true);
  };

  const closeDeleteModal = () => {
    setDeleteVisible(false);
    setDeleteLoading(false);
    setDeletingMember(null);
    deleteForm.resetFields();
  };

  const submitDelete = () => {
    if (!deletingMember?.id) {
      Message.error('会员ID不存在！');
      return;
    }
    deleteForm
      .validate()
      .then(values => {
        setDeleteLoading(true);
        post(`/review/userMember/delete/${deletingMember.id}`, {
          remark: values.remark.trim()
        })
          .then((res: any) => {
            const { code, message } = res || {};
            if (code === 200) {
              Message.success('删除成功！');
              closeDeleteModal();
              getList();
            } else {
              Message.error(message || '删除失败！');
            }
          })
          .catch(() => {
            Message.error('网络错误!');
          })
          .finally(() => setDeleteLoading(false));
      })
      .catch(() => {
        Message.error('请输入删除备注');
      });
  };

  const columns: TableColumnProps[] = useMemo(
    () => [
      {
        title: '序号',
        width: 70,
        fixed: 'left',
        render: (_text, _record, idx) => (pageNum - 1) * pageSize + idx + 1
      },
      {
        title: 'openId',
        dataIndex: 'openId',
        width: 170
      },
      {
        title: 'oneId',
        dataIndex: 'oneId',
        width: 120,
        render: text => (text ? text : '-')
      },
      {
        title: '用户名',
        dataIndex: 'gameName',
        width: 120
      },
      {
        title: '手机号',
        dataIndex: 'phone',
        width: 160,
        render: (text: string) => <PhoneCell phone={text} />
      },
      {
        title: '订单号',
        dataIndex: 'orderId',
        width: 160
      },
      {
        title: '支付状态',
        dataIndex: 'orderStatus',
        width: 120,
        render: (text: string) => {
          if (!text) return '-';
          const label = orderStatusLabelByValue[text] || text;
          let color = '#F2F3F5';
          let textColor = '#86909C';
          if (text === 'SUCCESS') {
            color = '#E8FFEA';
            textColor = '#00B42A';
          } else if (text === 'NOTPAY') {
            color = '#FFF7E8';
            textColor = '#FF7D00';
          } else if (text === 'REFUND') {
            color = '#FFECE8';
            textColor = '#F53F3F';
          }
          return (
            <Tag color={color} style={{ color: textColor, border: 'none' }}>
              {label}
            </Tag>
          );
        }
      },
      {
        title: '卡号',
        dataIndex: 'cardNo',
        width: 140
      },
      {
        title: '会员卡类型',
        dataIndex: 'cardType',
        width: 140,
        render: (text: CardType) => {
          const info = getCardTypeInfo(text);
          return (
            <Tag
              color={info.bgColor}
              style={{ color: info.color, border: 'none' }}
            >
              {info.label}
            </Tag>
          );
        }
      },
      {
        title: '会员卡状态',
        dataIndex: 'cardStatus',
        width: 130,
        render: (text: number) => (
          <Tag color="#F2F3F5" style={{ color: '#86909C', border: '1px solid #F2F3F5' }}>
            {text === 1 ? '已使用' : '-'}
          </Tag>
        )
      },
      {
        title: '开通时间',
        dataIndex: 'openTime',
        width: 140,
        render: text => (text ? dayjs(text).format('YYYY-MM-DD') : '-')
      },
      {
        title: '到期时间',
        dataIndex: 'expireTime',
        width: 140,
        render: text => (text ? dayjs(text).format('YYYY-MM-DD') : '-')
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        width: 180,
        render: text => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-')
      },
      {
        title: '复盘群',
        dataIndex: 'reviewGroupId',
        width: 160,
        ellipsis: true,
        render: (reviewGroupId: number | undefined) => getReviewGroupName(reviewGroupId)
      },
      {
        title: '是否删除',
        dataIndex: 'isValid',
        width: 130,
        render: (_text, record: UmsUserMember) => {
          const isDeleted = record.isValid === 0;
          return (
            <Tag
              color={isDeleted ? '#F2F3F5' : '#E8F3FF'}
              style={{
                color: isDeleted ? '#86909C' : '#2F6BFF',
                border: 'none'
              }}
            >
              {isDeleted ? '已删除' : '未删除'}
            </Tag>
          );
        }
      },
      {
        title: '操作',
        width: 180,
        fixed: 'right',
        render: (_text, record: UmsUserMember) =>
          record.isValid === 0 ? null : (
            <>
              <Button
                type="text"
                size="small"
                loading={editGroupLoading && editingMember?.id === record.id}
                onClick={() => openEditGroupModal(record)}
              >
                修改群组
              </Button>
              <Button
                type="text"
                status="danger"
                size="small"
                loading={deleteLoading && deletingMember?.id === record.id}
                onClick={() => openDeleteModal(record)}
              >
                删除
              </Button>
            </>
          )
      }
    ],
    [
      pageNum,
      pageSize,
      deleteLoading,
      deletingMember?.id,
      editGroupLoading,
      editingMember?.id,
      getReviewGroupName,
      getCardTypeInfo
    ]
  );

  return (
    <div className="user-member-management table-flex">
      <Space style={{ flexWrap: 'wrap', marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Form form={filterForm} layout="inline" autoComplete="off" style={{ flexWrap: 'wrap' }}>
          <Form.Item field="gameName" style={{ marginRight: 16 }}>
            <Input style={{ width: 120 }} placeholder="请输入用户名" allowClear />
          </Form.Item>
          <Form.Item field="phone" style={{ marginRight: 16 }}>
            <Input style={{ width: 120 }} placeholder="请输入手机号" allowClear />
          </Form.Item>
          <Form.Item field="openId" style={{ marginRight: 16 }}>
            <Input
              style={{ width: 150 }}
              placeholder="请输入 openId"
              allowClear
              onPressEnter={handleQuery}
            />
          </Form.Item>
          <Form.Item field="oneId" style={{ marginRight: 16 }}>
            <Input
              style={{ width: 150 }}
              placeholder="请输入 oneId"
              allowClear
              onPressEnter={handleQuery}
            />
          </Form.Item>
          <Form.Item field="orderId" style={{ marginRight: 16 }}>
            <Input style={{ width: 120 }} placeholder="请输入订单号" allowClear />
          </Form.Item>

          <Form.Item field="orderStatus" style={{ marginRight: 16 }}>
            <Select style={{ width: 120 }} placeholder="支付状态" allowClear>
              {orderStatusFilterOptions.map(item => (
                <Option key={item.value || 'all'} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item field="cardType" style={{ marginRight: 16 }}>
            <Select style={{ width: 120 }} placeholder="会员卡类型" allowClear>
              <Option value="">全部</Option>
              {memberCardList.map(item => (
                <Option key={item.id} value={item.cardType}>
                  {item.cardName || cardTypeMap[item.cardType as CardType]?.label || '-'}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item field="reviewGroupId" style={{ marginRight: 16 }}>
            <Select style={{ width: 120 }} placeholder="复盘群组" allowClear>
              {renderReviewGroupOptions()}
            </Select>
          </Form.Item>

          <Form.Item label="开通时间" field="openTimeRange" style={{ marginRight: 16 }}>
            <RangePicker style={{ width: 200 }} placeholder={['开始日期', '结束日期']} />
          </Form.Item>

          <Form.Item label="过期时间" field="expireTimeRange" style={{ marginRight: 16 }}>
            <RangePicker style={{ width: 200 }} placeholder={['开始日期', '结束日期']} />
          </Form.Item>

          <Form.Item field="isValid" style={{ marginRight: 16 }}>
            <Select style={{ width: 160 }} placeholder="是否删除" allowClear>
              <Option value="">
                全部
              </Option>
              {/* 后端：true=未删除(is_valid=1)，false=已删除(is_valid=0) */}
              <Option value="true">未删除</Option>
              <Option value="false">已删除</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" onClick={handleQuery} loading={loading}>
              查询
            </Button>
          </Form.Item>

          <Form.Item>
            <Button type="outline" onClick={handleReset} loading={loading}>
              重置
            </Button>
          </Form.Item>
        </Form>

        <Button className="filter-item" type="primary" onClick={openAddModal}>
          添加复盘会员
        </Button>
      </Space>

      <div className="table-content">
        <Table
          rowKey="id"
          columns={columns}
          data={dataList}
          className="table-scroll"
          loading={loading}
          pagination={{
            sizeCanChange: true,
            pageSize,
            total,
            showTotal: true,
            current: pageNum,
            sizeOptions: [20, 50, 100],
            onChange: (num: number, size?: number) => {
              setPageNum(num);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            }
          }}
          scroll={{ y: '100%', x: '100%' }}
        />
      </div>

      {/* 添加会员弹框 */}
      <Modal
        title="添加复盘会员"
        visible={addVisible}
        onCancel={closeAddModal}
        onOk={submitAdd}
        unmountOnExit
        style={{ width: 560 }}
        confirmLoading={addConfirmLoading}
      >
        <Form layout="vertical" form={addForm} autoComplete="off">
          {/* openId 通过 Select 选中后回填 */}
          <Form.Item
            style={{ display: 'none' }}
            field="openId"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="用户名（搜索）" required>
            <Select
              showSearch
              value={selectedUser?.value}
              placeholder="请输入用户名搜索"
              options={userOptions as any}
              filterOption={false}
              notFoundContent={
                userFetching ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin style={{ margin: 12 }} />
                  </div>
                ) : null
              }
              onSearch={debouncedFetchUsers}
              onChange={(val: string) => {
                const found = userOptions.find(u => u.value === val) || null;
                setSelectedUser(found);
                addForm.setFieldsValue({
                  openId: val,
                  gameName: found?.label || ''
                });
              }}
            />
          </Form.Item>
          <Form.Item label="用户手机号(只读)">
            <PhoneCell phone={selectedUser?.phone} />
          </Form.Item>

          <Form.Item label="openid(只读)">
            <Input value={selectedUser?.value || ''} disabled />
          </Form.Item>
          <Form.Item
            label="会员卡类型"
            field="cardType"
            rules={[{ required: true, message: '请选择会员卡类型' }]}
          >
            <Select
              placeholder="请选择会员卡类型"
              onChange={(val: CardType) => {
                if (val === MONTHLY_CARD_TYPE) {
                  addForm.setFieldValue('reviewGroupId', undefined);
                }
              }}
            >
              {enabledMemberCards.map(item => (
                <Option key={item.id} value={item.cardType}>
                  {item.cardName || cardTypeMap[item.cardType as CardType]?.label || '-'}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="复盘群"
            field="reviewGroupId"
            rules={
              isMonthlyCard ? [] : [{ required: true, message: '请选择复盘群' }]
            }
          >
            <Select
              placeholder={isMonthlyCard ? '月卡无需选择复盘群' : '请选择复盘群'}
              disabled={isMonthlyCard}
              allowClear={!isMonthlyCard}
            >
              {renderReviewGroupOptions()}
            </Select>
          </Form.Item>

          <Form.Item label="订单号" field="orderId">
            <Input readOnly placeholder="系统自动生成" />
          </Form.Item>

          <Form.Item label="备注" field="remark">
            <Input placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="修改群组"
        visible={editGroupVisible}
        onCancel={closeEditGroupModal}
        onOk={submitEditGroup}
        confirmLoading={editGroupLoading}
        unmountOnExit
        style={{ width: 480 }}
      >
        <Form layout="vertical" form={editGroupForm} autoComplete="off">
          <Form.Item label="用户名">
            <Input value={editingMember?.gameName || ''} disabled />
          </Form.Item>
          <Form.Item label="当前复盘群">
            <Input value={getReviewGroupName(editingMember?.reviewGroupId)} disabled />
          </Form.Item>
          <Form.Item
            label="目标复盘群"
            field="reviewGroupId"
            rules={[{ required: true, message: '请选择目标复盘群' }]}
          >
            <Select placeholder="请选择目标复盘群">
              {renderReviewGroupOptions(editingMember?.reviewGroupId)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="删除复盘会员"
        visible={deleteVisible}
        onCancel={closeDeleteModal}
        onOk={submitDelete}
        confirmLoading={deleteLoading}
        unmountOnExit
        style={{ width: 480 }}
      >
        <Form layout="vertical" form={deleteForm} autoComplete="off">
          <Form.Item label="用户名">
            <Input value={deletingMember?.gameName || ''} disabled />
          </Form.Item>
          <Form.Item
            label="删除备注"
            field="remark"
            rules={[
              { required: true, message: '请输入删除备注' },
              {
                validator: (value, callback) => {
                  if (!value?.trim()) {
                    callback('请输入删除备注');
                  } else {
                    callback();
                  }
                }
              },
              { maxLength: 500, message: '备注不能超过500个字符' }
            ]}
          >
            <Input.TextArea placeholder="请输入删除原因备注" maxLength={500} showWordLimit />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserMember;

